import { useState, useEffect } from 'react';
import { getProperties, createProperty, updateProperty, deleteProperty, getUnits, createUnit, deleteUnit } from '../services/api';
import { Buildings, Plus, Trash, PencilSimple, House, Warehouse, MapPin, X, CaretDown, CaretUp } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [expandedProperty, setExpandedProperty] = useState(null);
  const [units, setUnits] = useState({});

  const loadProperties = async () => {
    try {
      const res = await getProperties();
      setProperties(res.data);
    } catch (err) {
      console.error('Failed to load properties:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProperties();
  }, []);

  const loadUnits = async (propertyId) => {
    try {
      const res = await getUnits(propertyId);
      setUnits(prev => ({ ...prev, [propertyId]: res.data }));
    } catch (err) {
      console.error('Failed to load units:', err);
    }
  };

  const toggleExpand = async (propertyId) => {
    if (expandedProperty === propertyId) {
      setExpandedProperty(null);
    } else {
      setExpandedProperty(propertyId);
      if (!units[propertyId]) {
        await loadUnits(propertyId);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diese Immobilie wirklich löschen?')) return;
    try {
      await deleteProperty(id);
      loadProperties();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleDeleteUnit = async (unitId, propertyId) => {
    if (!window.confirm('Möchten Sie diese Einheit wirklich löschen?')) return;
    try {
      await deleteUnit(unitId);
      loadUnits(propertyId);
      loadProperties();
    } catch (err) {
      console.error('Failed to delete unit:', err);
    }
  };

  const getPropertyIcon = (type) => {
    switch (type) {
      case 'house': return <House weight="bold" className="w-5 h-5" />;
      case 'commercial': return <Warehouse weight="bold" className="w-5 h-5" />;
      default: return <Buildings weight="bold" className="w-5 h-5" />;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '—';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="properties-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">Immobilien</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Verwalten Sie Ihre Immobilien und Einheiten</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-slate-900 text-white px-4 sm:px-6 py-2.5 font-medium hover:bg-slate-800 flex items-center justify-center gap-2 w-full sm:w-auto"
          data-testid="add-property-button"
        >
          <Plus weight="bold" className="w-4 h-4" />
          Immobilie hinzufügen
        </button>
      </div>

      {/* Properties List */}
      {properties.length === 0 ? (
        <div className="bg-white border border-gray-200 p-8 sm:p-12 text-center">
          <Buildings weight="light" className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading text-lg sm:text-xl font-bold text-slate-900 mb-2">Keine Immobilien</h3>
          <p className="text-slate-500 mb-6 text-sm sm:text-base">Fügen Sie Ihre erste Immobilie hinzu, um zu starten.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-slate-900 text-white px-6 py-2.5 font-medium hover:bg-slate-800 inline-flex items-center gap-2"
          >
            <Plus weight="bold" className="w-4 h-4" />
            Erste Immobilie hinzufügen
          </button>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {properties.map((property) => (
            <div key={property.id} className="bg-white border border-gray-200" data-testid={`property-card-${property.id}`}>
              {/* Property Header */}
              <div 
                className="px-4 sm:px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpand(property.id)}
              >
                <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 flex-shrink-0 flex items-center justify-center text-slate-600">
                    {getPropertyIcon(property.type)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading text-base sm:text-lg font-bold text-slate-900 truncate">{property.name}</h3>
                    <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-slate-500">
                      <MapPin weight="regular" className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">{property.address}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-6 flex-shrink-0 ml-2">
                  <div className="text-right hidden sm:block">
                    <p className="text-xs tracking-widest uppercase font-bold text-slate-500">Einheiten</p>
                    <p className="font-mono text-xl sm:text-2xl font-bold text-slate-900">{property.units_count}</p>
                  </div>
                  <div className="sm:hidden text-center">
                    <p className="font-mono text-lg font-bold text-slate-900">{property.units_count}</p>
                    <p className="text-xs text-slate-500">Einh.</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingProperty(property); setShowAddModal(true); }}
                      className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                      data-testid={`edit-property-${property.id}`}
                    >
                      <PencilSimple weight="bold" className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(property.id); }}
                      className="p-1.5 sm:p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      data-testid={`delete-property-${property.id}`}
                    >
                      <Trash weight="bold" className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    {expandedProperty === property.id ? (
                      <CaretUp weight="bold" className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                    ) : (
                      <CaretDown weight="bold" className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Units */}
              {expandedProperty === property.id && (
                <div className="border-t border-gray-200 bg-gray-50">
                  <div className="px-4 sm:px-6 py-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-xs tracking-widest uppercase font-bold text-slate-500">Einheiten</h4>
                      <UnitAddDialog propertyId={property.id} onAdd={() => { loadUnits(property.id); loadProperties(); }} />
                    </div>
                    {units[property.id]?.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {units[property.id].map((unit) => (
                          <div key={unit.id} className="bg-white border border-gray-200 p-3 sm:p-4" data-testid={`unit-card-${unit.id}`}>
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium text-slate-900 text-sm sm:text-base">{unit.name}</h5>
                              <button
                                onClick={() => handleDeleteUnit(unit.id, property.id)}
                                className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                                data-testid={`delete-unit-${unit.id}`}
                              >
                                <Trash weight="bold" className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="text-xs sm:text-sm text-slate-500 space-y-1">
                              {unit.floor && <p>Etage: {unit.floor}</p>}
                              {unit.size_sqm && <p>Größe: {unit.size_sqm} m²</p>}
                              {unit.rooms && <p>Zimmer: {unit.rooms}</p>}
                              {unit.rent_amount && <p className="font-mono font-medium text-slate-900">Miete: {formatCurrency(unit.rent_amount)}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">Keine Einheiten vorhanden.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Property Modal */}
      <PropertyModal
        open={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingProperty(null); }}
        property={editingProperty}
        onSave={() => { loadProperties(); setShowAddModal(false); setEditingProperty(null); }}
      />
    </div>
  );
}

function PropertyModal({ open, onClose, property, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    type: 'apartment_building',
    purchase_date: '',
    purchase_price: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (property) {
      setFormData({
        name: property.name || '',
        address: property.address || '',
        type: property.type || 'apartment_building',
        purchase_date: property.purchase_date || '',
        purchase_price: property.purchase_price || ''
      });
    } else {
      setFormData({ name: '', address: '', type: 'apartment_building', purchase_date: '', purchase_price: '' });
    }
  }, [property]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...formData,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        units_count: property?.units_count || 0
      };
      if (property) {
        await updateProperty(property.id, data);
      } else {
        await createProperty(data);
      }
      onSave();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="property-modal">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="font-heading text-lg sm:text-xl font-bold text-slate-900">
            {property ? 'Immobilie bearbeiten' : 'Neue Immobilie'}
          </h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
            <X weight="bold" className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
              placeholder="z.B. Hauptstraße 10"
              required
              data-testid="property-name-input"
            />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Adresse</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
              placeholder="Straße, PLZ Ort"
              required
              data-testid="property-address-input"
            />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Typ</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
              data-testid="property-type-select"
            >
              <option value="apartment_building">Mehrfamilienhaus</option>
              <option value="house">Einfamilienhaus</option>
              <option value="commercial">Gewerbeimmobilie</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Kaufdatum</label>
              <input
                type="date"
                value={formData.purchase_date}
                onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
                data-testid="property-date-input"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Kaufpreis (€)</label>
              <input
                type="number"
                value={formData.purchase_price}
                onChange={(e) => setFormData(prev => ({ ...prev, purchase_price: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
                placeholder="250000"
                data-testid="property-price-input"
              />
            </div>
          </div>
          <div className="flex gap-3 sm:gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-200 text-slate-900 py-2.5 font-medium hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-slate-900 text-white py-2.5 font-medium hover:bg-slate-800 disabled:opacity-50"
              data-testid="property-save-button"
            >
              {loading ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UnitAddDialog({ propertyId, onAdd }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', floor: '', size_sqm: '', rooms: '', rent_amount: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createUnit({
        property_id: propertyId,
        name: formData.name,
        floor: formData.floor ? parseInt(formData.floor) : null,
        size_sqm: formData.size_sqm ? parseFloat(formData.size_sqm) : null,
        rooms: formData.rooms ? parseFloat(formData.rooms) : null,
        rent_amount: formData.rent_amount ? parseFloat(formData.rent_amount) : null
      });
      setFormData({ name: '', floor: '', size_sqm: '', rooms: '', rent_amount: '' });
      setOpen(false);
      onAdd();
    } catch (err) {
      console.error('Failed to create unit:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1" data-testid="add-unit-button">
          <Plus weight="bold" className="w-4 h-4" />
          Einheit hinzufügen
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white border-0 rounded-none">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold">Neue Einheit</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Name/Nummer</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
              placeholder="z.B. Wohnung 1A"
              required
              data-testid="unit-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Etage</label>
              <input
                type="number"
                value={formData.floor}
                onChange={(e) => setFormData(prev => ({ ...prev, floor: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                data-testid="unit-floor-input"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Größe (m²)</label>
              <input
                type="number"
                step="0.01"
                value={formData.size_sqm}
                onChange={(e) => setFormData(prev => ({ ...prev, size_sqm: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                data-testid="unit-size-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Zimmer</label>
              <input
                type="number"
                step="0.5"
                value={formData.rooms}
                onChange={(e) => setFormData(prev => ({ ...prev, rooms: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                data-testid="unit-rooms-input"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Miete (€)</label>
              <input
                type="number"
                step="0.01"
                value={formData.rent_amount}
                onChange={(e) => setFormData(prev => ({ ...prev, rent_amount: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                data-testid="unit-rent-input"
              />
            </div>
          </div>
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex-1 border border-gray-200 text-slate-900 py-2.5 font-medium hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-slate-900 text-white py-2.5 font-medium hover:bg-slate-800 disabled:opacity-50"
              data-testid="unit-save-button"
            >
              {loading ? 'Wird erstellt...' : 'Erstellen'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
